/**
 * Camera Service - Handles camera, photo library, video, and audio operations
 * 
 * Migration Status:
 * - ✅ Image capture: Uses @capacitor/camera (all platforms including web)
 * - ✅ Image selection: @capawesome/capacitor-file-picker (native paths) OR @capacitor/camera chooseFromGallery blob path for crop UIs (check-in, profile)
 * - ✅ Video capture: Uses VideoRecorderComponent with @capacitor-community/video-recorder (native paths)
 * - ✅ Video selection: Uses @capawesome/capacitor-file-picker (native paths via selectVideoAsNative/selectAttachmentAsNative)
 * - ✅ Audio recording: Uses capacitor-voice-recorder (native paths via captureAudioAsNative)
 * 
 * ✅ Now using:
 *    - @capacitor/camera for image capture and gallery selection for crop UIs (blobs via chooseFromGallery)
 *    - @capawesome/capacitor-file-picker for image/video selection (native paths)
 *    - VideoRecorderComponent for video capture (native paths)
 *    - capacitor-voice-recorder for audio recording (native paths)
 */

import { Injectable } from '@angular/core';
import { Platform, ModalController } from '@ionic/angular';
import * as Sentry from "@sentry/angular-ivy";
import { Camera as CapacitorCamera, CameraErrorCode, CameraResultType, CameraSource, MediaTypeSelection } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { SafeUrl } from '@angular/platform-browser';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { VoiceRecorder } from 'capacitor-voice-recorder';

declare const window: any; // Declare Cordova global functions

export interface LocalAttachment {
  name: string;
  type: "image" | "video" | "file" | "audio";
  file?: File | Blob;
  nativePath?: string;
  url?: string | SafeUrl;
}

export interface NativeMediaAttachment {
  name: string;
  nativePath: string;
  webUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class CameraService {
  private currentRecordingUri: string | null = null;
  
  constructor(
    public platform: Platform,
    private modalCtrl: ModalController
  ) { }

  // Note: Capacitor plugins (@capacitor/camera, @capawesome/capacitor-file-picker, capacitor-voice-recorder)
  // handle permissions automatically when used. No explicit permission checking needed.


  async captureImageAsBlob(options: {targetWidth?: number, targetHeight?: number, quality?: number} = {}): Promise<{name: string, blob: Blob}> {
    try {
      // Use Capacitor Camera - works on all platforms (native and web)
      // Convert quality from 0-100 scale (Capacitor uses 0-100)
      const quality = options.quality ? Math.min(100, Math.max(0, options.quality)) : 90;
      
      const photo = await CapacitorCamera.getPhoto({
        quality: quality,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        width: options.targetWidth || 2000,
        height: options.targetHeight || 2000
      });

      if (!photo.webPath) {
        throw new Error('No photo path returned');
      }

      // Convert the photo URI to a Blob
      const response = await fetch(photo.webPath);
      let blob = await response.blob();
      // Android WebView often returns an empty MIME type for capacitor:// paths
      if (!blob.type || !blob.type.startsWith('image/')) {
        blob = new Blob([blob], { type: 'image/jpeg' });
      }
      
      return {
        name: `${new Date().getTime()}.jpg`,
        blob: blob
      };
    } catch (error) {
      // Handle cancellation
      if (error && (error.message === 'User cancelled photos app' || error.message === 'User cancelled')) {
        throw {
          code: 3,
          message: "Canceled"
        };
      }
      Sentry.captureMessage("Error capturing image: " + JSON.stringify(error));
      throw error;
    }
  }

  /**
   * Capture image using camera and return native path
   * Returns native path for direct upload without blob conversion
   * @param options - Optional image capture options
   * @returns Promise that resolves with NativeMediaAttachment or null if cancelled
   */
  async captureImageAsNative(options: {targetWidth?: number, targetHeight?: number, quality?: number} = {}): Promise<NativeMediaAttachment | null> {
    try {
      // Verify we're on a native platform
      if (!Capacitor.isNativePlatform()) {
        console.warn('[CameraService] Not on native platform, Camera plugin may not work');
      }
      
      // Use Capacitor Camera - works on all platforms (native and web)
      // Convert quality from 0-100 scale (Capacitor uses 0-100)
      const quality = options.quality ? Math.min(100, Math.max(0, options.quality)) : 90;
      
      console.log('[CameraService] Platform:', Capacitor.getPlatform());
      console.log('[CameraService] Attempting to capture image...');
      
      const photo = await CapacitorCamera.getPhoto({
        quality: quality,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        width: options.targetWidth || 2000,
        height: options.targetHeight || 2000
      });

      if (!photo.webPath) {
        throw new Error('No photo path returned');
      }

      // Extract native path from Capacitor URL
      // On native platforms, photo.path contains the native file path
      // If path is not available, extract from webPath (capacitor://localhost/_capacitor_file_/path)
      let nativePath: string;
      if (photo.path) {
        nativePath = photo.path;
      } else if (photo.webPath) {
        // Extract native path from capacitor:// URL
        nativePath = photo.webPath.replace('capacitor://localhost/_capacitor_file_', '');
      } else {
        throw new Error('No native path available');
      }

      // Strip file:// prefix if present (upload plugin expects raw file system path)
      if (nativePath.startsWith('file://')) {
        nativePath = nativePath.replace('file://', '');
      }

      const name = `${new Date().getTime()}.jpg`;
      
      return {
        name: name,
        nativePath: nativePath,
        webUrl: Capacitor.convertFileSrc(photo.webPath) // Use webPath for preview
      };
    } catch (error) {
      // Handle cancellation
      if (error && (error as any).message && ((error as any).message === 'User cancelled photos app' || (error as any).message === 'User cancelled')) {
        return null;
      }
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Capture video using the VideoRecorderComponent modal
   * Opens the modal, waits for user to record, and returns the video data
   * @returns Promise that resolves with NativeMediaAttachment or null if cancelled
   */
  async captureVideoAsNative(): Promise<NativeMediaAttachment | null> {
    const { VideoRecorderComponent } = await import('../components/video-recorder/video-recorder.component');
    
    const modal = await this.modalCtrl.create({
      component: VideoRecorderComponent,
      cssClass: 'video-recorder-modal',
      backdropDismiss: false
    });
    
    await modal.present();
    
    const { data } = await modal.onDidDismiss();
    
    if (data && data.videoData && data.videoData.nativePath) {
      return {
        ...data.videoData,
        webUrl: Capacitor.convertFileSrc(data.videoData.videoUrl)
      }
    }
    
    return null;
  }

  /**
   * Select image from library using Capacitor File Picker
   * Returns native path for direct upload without blob conversion
   * @returns Promise that resolves with NativeMediaAttachment or null if cancelled
   */
  async selectImageAsNative(): Promise<NativeMediaAttachment | null> {
    try {
      const result = await FilePicker.pickImages({
        limit: 1,
        skipTranscoding: false
      });

      if (!result.files || result.files.length === 0) {
        return null;
      }

      const image = result.files[0];
      
      // Strip file:// prefix if present (upload plugin expects raw file system path)
      let nativePath = image.path;
      // IMPORTANT: Do not decode/mutate content:// URIs (Google Photos) — altering the string can break grants.
      if (!nativePath.startsWith('content://')) {
        // Decode percent-encoding (e.g., %20 -> space)
        nativePath = decodeURIComponent(nativePath);
        if (nativePath.startsWith('file://')) {
          nativePath = nativePath.replace('file://', '');
        }
      }
      
      const name = image.name || `${new Date().getTime()}.jpg`;
      
      return {
        name: name,
        nativePath: nativePath,
        webUrl: Capacitor.convertFileSrc(image.path) // Use original path with file:// for web preview
      };
    } catch (error) {
      // Handle cancellation
      if (error && (error as any).message && ((error as any).message.includes('cancel') || (error as any).message.includes('Cancel'))) {
        return null;
      }
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Select video from library using Capacitor File Picker
   * Returns native path for direct upload without blob conversion
   * @returns Promise that resolves with NativeMediaAttachment or null if cancelled
   */
  async selectVideoAsNative(): Promise<NativeMediaAttachment | null> {
    try {
      // skipTranscoding true returns quickly; we transcode in native code before upload (StorageService.fpPutNativeFile).
      const result = await FilePicker.pickVideos({
        limit: 1,
        skipTranscoding: true,
      });
  
      if (!result.files || result.files.length === 0) {
        return null;
      }
  
      const video = result.files[0];
      
      // Strip file:// prefix if present (upload plugin expects raw file system path)
      let nativePath = video.path;
      // IMPORTANT: Do not decode/mutate content:// URIs (Google Photos) — altering the string can break grants.
      if (!nativePath.startsWith('content://')) {
        // Decode percent-encoding (e.g., %20 -> space)
        nativePath = decodeURIComponent(nativePath);
        if (nativePath.startsWith('file://')) {
          nativePath = nativePath.replace('file://', '');
        }
      }
      
      const name = video.name || `${new Date().getTime()}.mp4`;
      
      return {
        name: name,
        nativePath: nativePath,
        webUrl: Capacitor.convertFileSrc(video.path) // Use original path with file:// for web preview
      };
    } catch (error) {
      // Handle cancellation
      if (error && (error as any).message && ((error as any).message.includes('cancel') || (error as any).message.includes('Cancel'))) {
        return null;
      }
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Select attachment (image or video) from library using Capacitor File Picker
   * Returns native path for direct upload without blob conversion
   * @param type - "image" or "video"
   * @returns Promise that resolves with NativeMediaAttachment or null if cancelled
   */
  async selectAttachmentAsNative(type: "image" | "video"): Promise<NativeMediaAttachment | null> {
    if (type === "image") {
      return this.selectImageAsNative();
    } else if (type === "video") {
      return this.selectVideoAsNative();
    } else {
      throw new Error(`Unsupported attachment type: ${type}. Only "image" and "video" are supported.`);
    }
  }

  /**
   * Start audio recording using capacitor-voice-recorder
   */
  async startRecord(): Promise<void> {
    try {
      // Check and request permissions
      const hasPermission = await VoiceRecorder.hasAudioRecordingPermission();
      if (!hasPermission.value) {
        const permissionResult = await VoiceRecorder.requestAudioRecordingPermission();
        if (!permissionResult.value) {
          throw new Error('Microphone permission denied');
        }
      }

      // VoiceRecorder handles permissions automatically

      // Check current status
      const status = await VoiceRecorder.getCurrentStatus();
      if (status.status === 'RECORDING') {
        throw new Error('Recording is already in progress');
      }

      // Start recording with directory option to get native file path
      // This will return a path instead of base64, which is more efficient
      await VoiceRecorder.startRecording({
        directory: Directory.Cache
      });
      this.currentRecordingUri = null; // Will be set when recording stops
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Stop audio recording (internal use)
   */
  async stopRecord(): Promise<void> {
    try {
      const status = await VoiceRecorder.getCurrentStatus();
      if (status.status !== 'RECORDING' && status.status !== 'PAUSED') {
        throw new Error('No active recording to stop');
      }

      const result = await VoiceRecorder.stopRecording();
      
      if (result.value?.path) {
        this.currentRecordingUri = result.value.path;
      }
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Capture audio and return native path
   * Stops current recording and returns NativeMediaAttachment
   */
  async captureAudioAsNative(): Promise<NativeMediaAttachment | null> {
    try {
      let audioPath: string | undefined;

      // Check if we have a stored path from a previous stopRecord() call
      if (this.currentRecordingUri) {
        audioPath = this.currentRecordingUri;
      } else {
        console.log('[CameraService] captureAudioAsNative: No stored URI, checking recording status');
        // Check if recording is active, if so stop it and get the path
        const status = await VoiceRecorder.getCurrentStatus();
        if (status.status === 'RECORDING' || status.status === 'PAUSED') {
          await this.stopRecord();
          audioPath = this.currentRecordingUri;
        } else {
          return null; // No recording available
        }
      }

      if (!audioPath) {
        return null;
      }

      // The plugin returns a path relative to Directory.Cache
      // Get the full URI from Filesystem for proper web URL construction
      const { uri } = await Filesystem.getUri({
        path: audioPath,
        directory: Directory.Cache
      });

      // Construct web-accessible URL for preview using Capacitor
      const webUrl = Capacitor.convertFileSrc(uri);

      // Strip file:// prefix if present for upload plugin
      let nativePath = uri;
      if (nativePath.startsWith('file://')) {
        nativePath = nativePath.replace('file://', '');
      }

      const fileName = audioPath.split('/').pop() || `audio_${new Date().getTime()}.aac`;

      return {
        name: fileName,
        nativePath: nativePath,
        webUrl: webUrl
      };
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Cancel/delete current recording
   * Note: capacitor-voice-recorder doesn't have a cancel method, so we just stop and clear
   */
  async deleteAudio(): Promise<void> {
    try {
      const status = await VoiceRecorder.getCurrentStatus();
      if (status.status === 'RECORDING' || status.status === 'PAUSED') {
        // Stop the recording (we can't cancel, but stopping will end it)
        await VoiceRecorder.stopRecording();
      }
      this.currentRecordingUri = null;
    } catch (error) {
      Sentry.captureException(error);
      // Don't throw - cleanup should be best effort
    }
  }

  /**
   * Select image from library and return a Blob (for crop modals, etc.).
   * Uses chooseFromGallery (system photo picker on Android — no READ_MEDIA_* permissions).
   */
  async selectImageAsBlob(
    options: {targetWidth?: number, targetHeight?: number, quality?: number} = {}
  ): Promise<{name: string, blob: Blob}> {
    try {
      const quality = options.quality ? Math.min(100, Math.max(0, options.quality)) : 90;

      const { results } = await CapacitorCamera.chooseFromGallery({
        mediaType: MediaTypeSelection.Photo,
        allowMultipleSelection: false,
        editable: 'no',
        quality,
        targetWidth: options.targetWidth || 2000,
        targetHeight: options.targetHeight || 2000,
      });

      const photo = results?.[0];
      if (!photo) {
        throw new Error('No photo returned');
      }

      let blob: Blob;
      if (photo.webPath) {
        const response = await fetch(photo.webPath);
        blob = await response.blob();
      } else if (photo.thumbnail) {
        // Web: chooseFromGallery returns the full image as base64 in thumbnail
        const dataUrl = photo.thumbnail.startsWith('data:')
          ? photo.thumbnail
          : `data:image/jpeg;base64,${photo.thumbnail}`;
        const response = await fetch(dataUrl);
        blob = await response.blob();
      } else {
        throw new Error('No photo path returned');
      }

      // Android WebView often returns an empty MIME type for capacitor:// paths
      if (!blob.type || !blob.type.startsWith('image/')) {
        blob = new Blob([blob], { type: 'image/jpeg' });
      }

      return {
        name: `${new Date().getTime()}.jpg`,
        blob,
      };
    } catch (error) {
      const err = error as { code?: string | number; message?: string };
      if (
        err?.code === CameraErrorCode.ChooseMediaCancelled ||
        err?.message === 'User cancelled photos app' ||
        err?.message === 'User cancelled'
      ) {
        throw {
          code: 3,
          message: 'Canceled',
        };
      }
      throw error;
    }
  }

  async getBlobFromDataURL(url: string): Promise<Blob> {
    const res = fetch(url);
    return (await res).blob();
  }

  // 📌 Convert `content://` to Blob (Android 10+ Scoped Storage)
  async contentUriToBlob(contentUri: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!window.resolveLocalFileSystemURL) {
        reject(new Error('Cordova is not available'));
        return;
      }
  
      window.resolveLocalFileSystemURL(contentUri, (fileEntry: any) => {
        fileEntry.file((file: any) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const blob = new Blob([reader.result as ArrayBuffer], { type: file.type });
            resolve(blob);
          };
  
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        }, reject);
      }, reject);
    });
  }

  // 📌 Convert `file://` to Blob (Standard iOS & Android)
  async fileUriToFileBlob(fileUri: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      window.resolveLocalFileSystemURL(fileUri, (fileEntry: any) => {
        fileEntry.file((file: any) => {
          const reader = new FileReader();

          reader.onloadend = () => {
            const blob = new Blob([reader.result as ArrayBuffer], { type: file.type });
            resolve(blob);
          };

          reader.onerror = (err) => reject(err);
          reader.readAsArrayBuffer(file);
        }, (err: any) => reject(err));
      }, (err: any) => reject(err));
    });
  }


  private async getFileFromUri(uri: string): Promise<File | Blob> {
    // Try using Capacitor Filesystem first for file:// URIs
    if (uri.startsWith('file://')) {
      try {
        alert('Trying Capacitor Filesystem for file:// URI: ' + uri);
        let fsPath = uri.replace('file://', '');
        if (fsPath.startsWith('/')) {
          fsPath = fsPath.substring(1);
        }
        const fileData = await Filesystem.readFile({
          path: fsPath,
          directory: Directory.External
        });
        const dataString = typeof fileData.data === 'string' ? fileData.data : '';
        if (dataString) {
          const binaryString = atob(dataString);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          alert('Capacitor Filesystem read successful');
          return new Blob([bytes.buffer], { type: 'video/mp4' });
        }
      } catch (fsError) {
        alert('Capacitor Filesystem failed for file:// URI: ' + JSON.stringify(fsError));
      }
    }

    // For content:// URIs, use Cordova API (not File plugin)
    if (uri.startsWith('content://')) {
      return await this.contentUriToBlob(uri);
    }

    // Fallback: Try direct File using Cordova API (not File plugin)
    try {
      const e: any = await new Promise((res, rej) => {
        window.resolveLocalFileSystemURL(uri, res, rej);
      });
      const f: File = await new Promise((res, rej) => e.file(res, rej));
      if (f && f.size > 0) {
        alert('Direct File read successful via Cordova API');
        return f;
      }
    } catch {}

    // Last resort: JS read via contentUriToBlob
    return await this.contentUriToBlob(uri);
  }
}
